import React, { useState, useRef, useEffect } from 'react';
import { Mail, Phone, MapPin, Send, CheckCircle } from 'lucide-react';
import { contactData } from '../data/mock';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import VisualCaptcha from './VisualCaptcha';
import axios from 'axios';

const Contact = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    countryCode: '+57',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isCaptchaVerified, setIsCaptchaVerified] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const currentSectionRef = sectionRef.current;
    if (currentSectionRef) {
      observer.observe(currentSectionRef);
    }

    return () => {
      if (currentSectionRef) {
        observer.unobserve(currentSectionRef);
      }
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCountryCodeChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      countryCode: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validación básica
    if (!formData.name || !formData.email || !formData.phone || !formData.message) {
      toast.error('Por favor completa todos los campos requeridos');
      return;
    }

    // Validación del captcha
    if (!isCaptchaVerified) {
      toast.error('Por favor completa la verificación de seguridad');
      return;
    }

    setIsSubmitting(true);

    try {
      const data = new FormData();
      data.append('name', formData.name);
      data.append('email', formData.email);
      data.append('phone', formData.phone); // Assuming phone includes country code or acts as full number
      data.append('message', formData.message);
      // Country code is manually handled in UI but expected as part of phone in backend or separate?
      // app/runner.php expects 'phone', so we just send that.

      await axios.post('/out.php', data);

      setIsSubmitting(false);
      setIsSuccess(true);
      toast.success('¡Mensaje enviado exitosamente!');

      // Construir mensaje de WhatsApp
      const whatsappMessage = `Hola BEZALEL TECHNOLOGY, mi nombre es ${formData.name}. ${formData.message}`;
      const whatsappUrl = `https://wa.me/${contactData.whatsapp.replace(/\+/g, '')}?text=${encodeURIComponent(whatsappMessage)}`;

      // Abrir WhatsApp después de un breve delay
      setTimeout(() => {
        window.open(whatsappUrl, '_blank');
      }, 1000);

      // Resetear formulario
      setFormData({
        name: '',
        email: '',
        phone: '',
        countryCode: '+57',
        message: ''
      });
      setIsCaptchaVerified(false);

      setTimeout(() => {
        setIsSuccess(false);
      }, 3000);

    } catch (error) {
      console.error('Error sending message:', error);
      setIsSubmitting(false);
      toast.error('Hubo un error al enviar el mensaje. Por favor intenta nuevamente.');
    }
  };

  const countryCodes = [
    { code: '+57', country: '🇨🇴 Colombia' },
    { code: '+1', country: '🇺🇸 Estados Unidos' },
    { code: '+52', country: '🇲🇽 México' },
    { code: '+34', country: '🇪🇸 España' },
    { code: '+54', country: '🇦🇷 Argentina' },
    { code: '+56', country: '🇨🇱 Chile' },
    { code: '+51', country: '🇵🇪 Perú' }
  ];

  return (
    <section id="contacto" ref={sectionRef} className="py-12 md:py-20 bg-gradient-to-b from-white to-gray-50 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 right-10 w-96 h-96 bg-cyan-100/30 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-72 h-72 bg-blue-100/30 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-5 sm:px-8 relative z-10">
        {/* Header */}
        <div
          className={`text-center mb-8 md:mb-16 transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 md:mb-6">
            <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">Contacto</span>
          </h2>
          <div className="w-20 md:w-24 h-1 bg-gradient-to-r from-blue-600 to-cyan-500 mx-auto mb-5 md:mb-8"></div>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-2xl mx-auto">
            ¿Listo para transformar tu negocio? Contáctanos y descubre cómo podemos ayudarte
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 md:gap-12 max-w-6xl mx-auto">
          {/* Contact Form */}
          <div
            className={`transform transition-all duration-1000 ${isVisible ? 'translate-x-0 opacity-100' : '-translate-x-10 opacity-0'
              }`}
          >
            <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-6 md:p-8 border border-gray-100">
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 md:mb-6">
                Envíanos un mensaje
              </h3>

              <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
                {/* Name */}
                <div>
                  <Label htmlFor="name" className="text-gray-700">
                    Nombre *
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="mt-2"
                    placeholder="Tu nombre completo"
                  />
                </div>

                {/* Email */}
                <div>
                  <Label htmlFor="email" className="text-gray-700">
                    Email *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="mt-2"
                    placeholder="tu@email.com"
                  />
                </div>

                {/* Phone */}
                <div>
                  <Label htmlFor="phone" className="text-gray-700">
                    Teléfono *
                  </Label>
                  <div className="flex flex-col sm:flex-row gap-2 mt-2">
                    <Select value={formData.countryCode} onValueChange={handleCountryCodeChange}>
                      <SelectTrigger className="w-full sm:w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {countryCodes.map((item) => (
                          <SelectItem key={item.code} value={item.code}>
                            {item.country}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="flex-1 w-full"
                      placeholder="3123456789"
                    />
                  </div>
                </div>

                {/* Message */}
                <div>
                  <Label htmlFor="message" className="text-gray-700">
                    Mensaje *
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    className="mt-2 min-h-24 md:min-h-32"
                    placeholder="Cuéntanos sobre tu proyecto..."
                  />
                </div>

                {/* Visual Captcha */}
                <VisualCaptcha onVerify={setIsCaptchaVerified} />

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={isSubmitting || isSuccess}
                  className="w-full bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white font-semibold py-6 text-lg"
                >
                  {isSubmitting ? (
                    'Enviando...'
                  ) : isSuccess ? (
                    <>
                      <CheckCircle className="w-5 h-5 mr-2" />
                      ¡Enviado!
                    </>
                  ) : (
                    <>
                      Enviar Mensaje
                      <Send className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
              </form>
            </div>
          </div>

          {/* Contact Info */}
          <div
            className={`min-w-0 transform transition-all duration-1000 delay-300 ${isVisible ? 'translate-x-0 opacity-100' : 'translate-x-10 opacity-0'
              }`}
          >
            {/* ── MOBILE: 2×2 compact grid ── */}
            <div className="grid grid-cols-2 gap-3 lg:hidden mb-3">

              {/* Email */}
              <div className="bg-white rounded-xl shadow-md p-3 border border-gray-100 flex flex-col items-center text-center gap-2">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-white" />
                </div>
                <div className="min-w-0 w-full">
                  <h4 className="text-xs font-semibold text-gray-900 mb-0.5">Email</h4>
                  <a href={`mailto:${contactData.email}`} className="text-blue-600 text-[11px] break-all hover:text-blue-700 leading-tight block">
                    {contactData.email}
                  </a>
                </div>
              </div>

              {/* Teléfono */}
              <div className="bg-white rounded-xl shadow-md p-3 border border-gray-100 flex flex-col items-center text-center gap-2">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h4 className="text-xs font-semibold text-gray-900 mb-0.5">Teléfono</h4>
                  <a href={`tel:${contactData.phone}`} className="text-blue-600 text-[11px] hover:text-blue-700 leading-tight block">
                    {contactData.phone}
                  </a>
                </div>
              </div>

              {/* Dirección */}
              <div className="bg-white rounded-xl shadow-md p-3 border border-gray-100 flex flex-col items-center text-center gap-2">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-lg flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h4 className="text-xs font-semibold text-gray-900 mb-0.5">Dirección</h4>
                  <p className="text-blue-600 text-[11px] leading-tight">{contactData.address}</p>
                </div>
              </div>

              {/* Horario */}
              <div className="bg-white rounded-xl shadow-md p-3 border border-gray-100 flex flex-col items-center text-center gap-2">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                </div>
                <div>
                  <h4 className="text-xs font-semibold text-gray-900 mb-0.5">Horario</h4>
                  <p className="text-blue-600 text-[11px] leading-tight">Lunes a Viernes</p>
                  <p className="text-gray-500 text-[11px] leading-tight">8:00 - 5:00 p.m.</p>
                </div>
              </div>
            </div>

            {/* ── DESKTOP: vertical cards (lg+) ── */}
            <div className="hidden lg:block space-y-6">
              <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-shadow">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Email</h4>
                    <a href={`mailto:${contactData.email}`} className="text-blue-600 hover:text-blue-700 transition-colors">{contactData.email}</a>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-shadow">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Teléfono</h4>
                    <a href={`tel:${contactData.phone}`} className="text-blue-600 hover:text-blue-700 transition-colors">{contactData.phone}</a>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-shadow">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Dirección</h4>
                    <p className="text-blue-600">{contactData.address}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-shadow">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-xl flex items-center justify-center flex-shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Horario de Atención</h4>
                    <p className="text-blue-600">Lunes a Viernes</p>
                    <p className="text-gray-600">8:00 a.m. - 5:00 p.m.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* WhatsApp CTA — full width on all screens */}
            <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-2xl shadow-xl p-5 md:p-8 text-white mt-3 md:mt-6">
              <h3 className="text-lg md:text-2xl font-bold mb-2 md:mb-3">¿Prefieres WhatsApp?</h3>
              <p className="mb-4 md:mb-6 text-sm md:text-base text-green-50">
                Chatea directamente con nuestro equipo para respuestas inmediatas
              </p>
                <Button
                  onClick={() => {
                    const message = 'Hola Bezalel Technology, estoy interesado en sus soluciones tecnológicas. ¡Quiero contactarlos!';
                    window.open(
                      `https://wa.me/${contactData.whatsapp.replace(/\+/g, '')}?text=${encodeURIComponent(message)}`,
                      '_blank'
                    );
                  }}
                  className="w-full bg-white text-green-600 hover:bg-green-50 font-semibold"
                  size="lg"
                >
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                  </svg>
                  Chatear en WhatsApp
                </Button>
              </div>
            </div>
          </div>
        </div>
    </section>
  );
};

export default Contact;
